import React, { useState, useEffect, useMemo, useCallback } from 'react'
import cn from 'classnames'
import { map, some, get, flatten, isNumber } from 'lodash'

import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import quantityValidator from '../../utils/quantityValidator'
import quantityUpdater from '../../utils/quantityUpdater'
import { NOTES_CHARACTER_LIMIT } from '../../constants'

import styles from './VNItemDetail.module.scss'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import ModifierGroup from '../../components/ModifierGroup'
import { Button, ButtonBase } from '@material-ui/core'
import SidebarModal from '../../components/SidebarModal'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { getItemForCurrentMenuById } from '../../selectors/items'
import { addToCart } from '../../actions/cart'
import { getIsKeyboardActive } from '../../VNAndroidSDK/Selectors'
import { isOpenPriceItem } from '../../utils/items'
import VNItemDetailOpenPrice from './VNItemDetailOpenPrice'
import { dollarsToCents } from '../../VNUtil/Money'
import { setManagerApprovalPrompt } from '../../VNManagerApproval/ActionCreators'
import { getManagerId } from '../../selectors/attendant'
import VNPriceIndicator from './VNPriceIndicator'
import { getLatestPriceAdjustment } from '../../VNDialogs/Selectors'
import { setPriceAdjustDialog } from '../../VNDialogs/ActionCreators'
import callable from '../../utils/callable'
import { APPLICATION_STRINGS } from '../../strings'
import { getBarcodeScanningSettings } from '../../selectors/config'
import { restartBarcodeScanner } from '../../actions/config'
import { isE700Device } from '../../VNAndroidSDK/Utils'

const textFieldStyles = makeStyles({
  root: {
    // focused color for input with variant='outlined'
    '& .MuiOutlinedInput-root': {
      '&.MuiInputBase-formControl fieldset': {
        border: 0,
      },
      '&.Mui-focused fieldset': {
        border: 0,
      },
    },
  },
})

/**
 * VNItemDetail - used to add Modifier items to cart
 */
const VNItemDetail = ({
  quantity = 1,
  notesAreEmpty = true,
  selectedItemIds = [],
  className,
  itemUuid = null,
  isOrderInProgress = false,
  closeSidebarHandler = null,
  buttonText = APPLICATION_STRINGS.ADD_TO_CART,
  onSubmitHandler = null,
  ...props
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { uuid } = useParams()

  const item = useSelector((state) => getItemForCurrentMenuById(state, uuid))
  const isKeyboardActive = useSelector(getIsKeyboardActive)
  const managerId = useSelector(getManagerId)

  const modifierGroups = get(item, 'modifierGroups', [])

  const id = item.id
  const title = item.name
  const isAlcohol = item.isAlcohol
  const canEnterNotes = item.canEnterNotes
  const orderMaxAmount = item.orderMaxAmount ?? 999
  const orderMinCount = item.orderMinCount || 1

  const classNames = cn(styles.cartItemDetailsModal, className)
  const classes = textFieldStyles()
  const isMobile = useGetIsMobileScreen()
  const defaultModifiers = useMemo(() => {
    const output = []
    modifierGroups.forEach((modifierGroup) => {
      const curGroup = []
      modifierGroup.defaults?.forEach((modId) => {
        curGroup.push(`${modId}|${modifierGroup.uuid}`)
      })
      output.push(curGroup)
    })
    return output
  }, [modifierGroups])

  const isOpenPrice = isOpenPriceItem(item)
  const [modifierGroupsSelections, setModifierGroupsSelections] = useState(defaultModifiers)
  const [validators, setValidators] = useState(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
  const [isValid, setIsValid] = useState(false)
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const [isQuantityUpdating, setIsQuantityUpdating] = useState(false)
  const [notes, setNotes] = useState(null)
  const [openPrice, setOpenPrice] = useState('0.00')
  const [openName, setOpenName] = useState(item.name)
  const [inputValue, setInputValue] = useState(quantity)
  const [inputPlaceholder, setInputPlaceholder] = useState(quantity)
  const clientHeight = document?.body?.clientHeight
  const isMaxQuantity = inputValue === orderMaxAmount
  const isMinQuantity = inputValue === orderMinCount
  const priceAdjustmentInCents = useSelector(getLatestPriceAdjustment)
  const barcodeScanningSettings = useSelector(getBarcodeScanningSettings)
  const hasPriceAdjustment = useMemo(() => {
    if (priceAdjustmentInCents === null) {
      return false
    }
    return priceAdjustmentInCents !== item?.defaultPriceInCents
  }, [priceAdjustmentInCents, item?.defaultPriceInCents])

  const submitButtonText = useMemo(() => {
    if (isOpenPrice) return 'Save'
    return buttonText
  }, [buttonText, isOpenPrice])

  const onSelect = (itemId, index) => {
    const modifierGroup = modifierGroups[index]
    const { min, max } = modifierGroup
    const selectedModifiers = modifierGroupsSelections?.[index]

    const nextSelectedModifiers = [...modifierGroupsSelections]
    nextSelectedModifiers[index] = quantityUpdater(min, max, selectedModifiers, itemId)

    setModifierGroupsSelections(nextSelectedModifiers)
  }

  useEffect(() => {
    setInputPlaceholder(quantity)
  }, [quantity])

  useEffect(() => {
    const validations = map(validators, (validator, index) => {
      const modifierGroupSelections = modifierGroupsSelections?.[index]

      return validator.isValid(get(modifierGroupSelections, 'length', 0))
    })

    const modifiersValid = !some(validations, (valid) => valid === false)
    const isValid = isOpenPrice ? modifiersValid && openPrice && openName : modifiersValid

    setIsValid(isValid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifierGroupsSelections, openPrice, openName])

  useEffect(() => {
    setValidators(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
  }, [modifierGroups])

  let notesElement = null

  const hasCharacterMax = isNumber(NOTES_CHARACTER_LIMIT)

  const handleSetNotes = (event) => {
    const notes = event.target.value || ''

    if (hasCharacterMax) {
      setNotes(notes.substring(0, NOTES_CHARACTER_LIMIT))
      return
    }

    setNotes(notes)
  }

  const onFocus = () => {
    if (!isQuantityUpdating) {
      setIsKeyboardOpen(true)
    }
  }
  const onBlur = () => {
    setIsKeyboardOpen(false)
  }
  // needs to be done at add to cart action
  if (canEnterNotes) {
    notesElement = (
      <div className={cn(styles.notesContainer, { [styles.emptyNotes]: notesAreEmpty })}>
        <TextField
          id="special-instructions"
          label="Add Special Instructions"
          value={notes}
          onChange={handleSetNotes}
          className={cn(styles.notes, classes.root)}
          InputLabelProps={{ className: styles.textfield__label }}
          inputProps={{ 'data-allow-keyboard-activity': true }}
          InputProps={{ className: styles.textfield__input }}
          variant="outlined"
          multiline
          onFocus={onFocus}
          onBlur={onBlur}
          rows={5}
        />
      </div>
    )
  }

  const handleSetOpenPrice = (openPrice) => {
    setOpenPrice(openPrice)
  }

  const handleSetOpenName = (openName) => {
    setOpenName(openName)
  }

  const incrementQuantity = () => {
    const newValue = inputValue === orderMaxAmount ? orderMaxAmount : inputValue + 1
    setInputValue(newValue)
  }

  const decrementQuantity = () => {
    const newValue = inputValue === orderMinCount ? orderMinCount : inputValue - 1
    setInputValue(newValue)
  }

  const handleInput = (e) => {
    const input = get(e, 'target.value', '')
    setInputValue(input)
  }

  const handleSetQuantity = (inputValue) => {
    let newValue = inputValue
    if (!inputValue || inputValue < orderMinCount) {
      newValue = orderMinCount
    }
    if (inputValue > orderMaxAmount) {
      newValue = orderMaxAmount
    }
    setInputValue(newValue)
  }

  const handleSubmit = (e) => {
    const action = (priceOverride = null, originalPriceInCents = null) => {
      setIsValid(false)

      if (onSubmitHandler) {
        callable(onSubmitHandler, priceOverride)
        return
      }

      dispatch(
        addToCart({
          itemId: id,
          modifiers: flatten(modifierGroupsSelections),
          quantity: inputValue,
          isAlcohol,
          notes,
          openPriceInCents: isOpenPrice ? dollarsToCents(openPrice) : priceOverride,
          originalPriceInCents,
          openName: isOpenPrice ? openName : null,
          managerId,
        }),
      )

      if (barcodeScanningSettings.barcodeScanningEnabled) {
        dispatch(restartBarcodeScanner(true))
      }

      history.goBack()
    }

    if (hasPriceAdjustment) {
      action(priceAdjustmentInCents, item.defaultPriceInCents)
      dispatch(setPriceAdjustDialog())
      return
    }

    if (!isOpenPrice) {
      action()
      return
    }

    dispatch(setManagerApprovalPrompt({ onSuccess: action, buttonText: 'Add to Cart' }))
  }

  const handleClose = useCallback(() => {
    history.goBack()

    if (barcodeScanningSettings.barcodeScanningEnabled) {
      dispatch(restartBarcodeScanner(true))
    }

    if (hasPriceAdjustment) {
      dispatch(setPriceAdjustDialog())
    }
  }, [dispatch, hasPriceAdjustment, history, barcodeScanningSettings])

  return (
    <SidebarModal show title="Item Detail" onClose={handleClose} className={cn(classNames, styles.header)} {...props}>
      <div className={styles.cartItemDetailsModal}>
        <div className={styles.scrollable} style={isKeyboardOpen ? { height: '80vh' } : null}>
          <div className={cn(styles.header, { [styles.noBorder]: isOpenPrice })}>
            <div className={styles.itemName}>{title}</div>
            <VNPriceIndicator priceAdjustmentInCents={priceAdjustmentInCents} defaultPriceInCents={item.defaultPriceInCents} />
          </div>
          {isOpenPrice && (
            <VNItemDetailOpenPrice
              openPrice={openPrice}
              handleSetOpenPrice={handleSetOpenPrice}
              openName={openName}
              handleSetOpenName={handleSetOpenName}
              itemName={item.name}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          )}
          {modifierGroups.map((modifierGroup, index) => {
            if (!modifierGroup.name) {
              return null
            }
            return (
              <ModifierGroup
                key={index}
                className={styles.modifierGroup}
                title={modifierGroup.name}
                label={get(validators, `[${index}].label`)}
                items={modifierGroup.items}
                selectedItemIds={modifierGroupsSelections?.[index]}
                isRequired={get(validators, `[${index}].validationType`) === quantityValidator.types.required}
                min={modifierGroup.min}
                max={modifierGroup.max}
                onSelect={(itemId) => onSelect(itemId, index)}
                uuid={modifierGroup.uuid}
              />
            )
          })}
          {notesElement}
        </div>

        {/* isKeyboardActive Remove this isMobile nonesense use css instead */}
        {(!isKeyboardActive || isQuantityUpdating) && (
          <div className={styles.footer} style={isMobile && isQuantityUpdating && clientHeight > 800 ? { top: '48vh' } : null}>
            <div className={styles.updateQuantity}>
              <ButtonBase
                disableRipple={isE700Device()}
                component="span"
                className={isMinQuantity ? cn(styles.disabled, styles.minus) : cn(styles.quantityOperator, styles.minus)}
                onClick={decrementQuantity}
              >
                –
              </ButtonBase>
              <input
                type="number"
                className={styles.quantity}
                placeholder={inputPlaceholder}
                value={inputValue}
                onChange={handleInput}
                onFocus={() => {
                  setIsQuantityUpdating(true)
                  setInputPlaceholder('')
                }}
                onBlur={() => {
                  setIsQuantityUpdating(false)
                  handleSetQuantity(+inputValue)
                }}
                pattern="\d*"
              />
              <ButtonBase
                disableRipple={isE700Device()}
                component="span"
                className={isMaxQuantity ? cn(styles.disabled, styles.plus) : cn(styles.quantityOperator, styles.plus)}
                onClick={incrementQuantity}
              >
                +
              </ButtonBase>
            </div>
            <div className={styles.container}>
              <Button className={styles.submit} disabled={!isValid} onClickCapture={handleSubmit}>
                <span>{submitButtonText}</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </SidebarModal>
  )
}

export default VNItemDetail
